import { makeStyles, tokens } from '@fluentui/react-components';

export const useEvalRunsPageStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    padding: '24px',
    gap: '20px',
    minWidth: 0,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
  },
  title: {
    fontSize: tokens.fontSizeBase600,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  subtitle: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
    marginTop: '6px',
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    flexWrap: 'wrap',
  },
  toolbarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
    flexGrow: 1,
  },
  toolbarRight: { display: 'flex', alignItems: 'center', gap: '8px' },
  searchInput: { minWidth: '220px', flexGrow: 1, flexBasis: '280px' },
  filterRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    padding: '12px',
    alignItems: 'center',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  filterField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    fontSize: tokens.fontSizeBase200,
    minWidth: '160px',
    flexGrow: 1,
    flexBasis: '160px',
    maxWidth: '280px',
  },
  list: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    overflowX: 'auto',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  listHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '12px 16px',
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  loadedRange: {
    padding: '0 16px 12px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    lineHeight: '20px',
  },
  table: { minWidth: '480px', tableLayout: 'fixed', width: '100%' },
  tableWithContext: { minWidth: '1100px' },
  tableHeader: {
    backgroundColor: tokens.colorBrandBackground2,
    '& th': {
      color: tokens.colorBrandForeground2,
      fontWeight: tokens.fontWeightSemibold,
    },
  },
  nameColumn: { paddingLeft: '16px' },
  statusColumn: { width: '140px' },
  pipelineColumn: { width: '220px' },
  branchColumn: { width: '160px' },
  dateColumn: { width: '180px' },
  cell: { padding: '8px 16px' },
  row: {
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  rowName: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    display: 'block',
    textAlign: 'left',
    padding: 0,
    minHeight: '20px',
    minWidth: 0,
    maxWidth: '100%',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    lineHeight: '20px',
    ':hover': { color: tokens.colorBrandForegroundLink },
  },
  primaryText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  rowMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginTop: '2px',
    lineHeight: '16px',
  },
  historyToggle: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    minHeight: '24px',
    padding: '0 4px',
  },
  historyCell: {
    padding: '12px 20px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderLeft: `3px solid ${tokens.colorBrandStroke1}`,
  },
  historyCaption: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    paddingBottom: '8px',
    fontWeight: tokens.fontWeightSemibold,
  },
  historyList: { listStyleType: 'none', padding: 0, margin: 0 },
  historyRun: {
    display: 'grid',
    gridTemplateColumns: 'minmax(180px, 1fr) minmax(240px, 1fr) auto',
    alignItems: 'center',
    gap: '16px',
    width: '100%',
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    textAlign: 'left',
    padding: '4px 8px',
    ':hover': {
      backgroundColor: tokens.colorBrandBackground2,
    },
    '@media (max-width: 700px)': {
      gridTemplateColumns: 'minmax(0, 1fr) auto',
      rowGap: '4px',
    },
  },
  historyCreator: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: 0,
    '@media (max-width: 700px)': {
      gridColumn: '1',
      gridRow: '2',
    },
  },
  historyAction: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '8px',
    whiteSpace: 'nowrap',
    '@media (max-width: 700px)': {
      gridColumn: '2',
      gridRow: '1 / span 2',
    },
  },
  historyReport: {
    color: tokens.colorBrandForegroundLink,
    fontWeight: tokens.fontWeightSemibold,
  },
  loadMore: { alignSelf: 'center' },
  statusBadge: {
    justifyContent: 'center',
    boxSizing: 'border-box',
    width: '108px',
    height: '28px',
  },
  statusIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    lineHeight: 0,
    '& svg': { display: 'block' },
  },
  statusIconSpinning: {
    animationName: {
      from: { transform: 'rotate(0deg)' },
      to: { transform: 'rotate(360deg)' },
    },
    animationDuration: '1.4s',
    animationTimingFunction: 'linear',
    animationIterationCount: 'infinite',
    '@media (prefers-reduced-motion: reduce)': { animationName: 'none' },
  },
  branch: {
    display: 'block',
    maxWidth: '170px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
  },
  emptyState: {
    padding: '48px',
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
  },
});
